import type { HexCoord } from '../../shared/hex';
import type {
  GameState,
  PlayerId,
  SolarSystemMap,
} from '../../shared/types/domain';
import {
  type CombatTargetPlan,
  createCombatTargetPlan,
  getCombatAttackerIdAtHex,
  getCombatTargetAtHex,
  toggleCombatAttackerSelection,
} from './combat';
import type { GameCommand } from './commands';
import { resolveAstrogationClick, resolveOrdnanceClick } from './input';
import type { InteractionMode } from './interaction-fsm';
import type {
  AstrogationPlanningSnapshot,
  CombatPlanningSnapshot,
  InteractivePlanningSnapshot,
  OrdnancePlanningSnapshot,
} from './planning';

export type InputEvent =
  | { type: 'clickHex'; hex: HexCoord; shiftKey?: boolean }
  | { type: 'hoverHex'; hex: HexCoord | null };

const interpretCombatClick = (
  hex: HexCoord,
  state: GameState,
  map: SolarSystemMap | null,
  playerId: PlayerId,
  planning: CombatPlanningSnapshot,
): GameCommand[] => {
  // Check enemy targets first so mixed hexes prioritise
  // target selection over friendly attacker toggling.
  const target = getCombatTargetAtHex(
    state,
    playerId,
    hex,
    map,
    planning.queuedAttacks,
    planning.combatTargetId,
  );

  if (target) {
    const isSame =
      planning.combatTargetId === target.targetId &&
      planning.combatTargetType === target.targetType;

    if (isSame) {
      return [{ type: 'clearCombatSelection' }];
    }

    const plan = createCombatTargetPlan(
      state,
      playerId,
      planning,
      target.targetId,
      target.targetType,
      map,
    );

    return [{ type: 'setCombatPlan', plan }];
  }

  const attackerId = getCombatAttackerIdAtHex(
    state,
    playerId,
    hex,
    planning.selectedShipId,
  );

  if (attackerId) {
    const toggle = toggleCombatAttackerSelection(
      state,
      playerId,
      planning,
      map,
      attackerId,
    );

    if (toggle?.consumed) {
      const plan: CombatTargetPlan = {
        combatTargetId: planning.combatTargetId,
        combatTargetType: planning.combatTargetType,
        combatAttackerIds: toggle.combatAttackerIds,
        combatAttackStrength: toggle.combatAttackStrength,
      };

      return [
        {
          type: 'setCombatPlan',
          plan,
          selectedShipId: attackerId,
        },
      ];
    }
  }

  return [{ type: 'clearCombatSelection' }];
};

const interpretAstrogationClick = (
  hex: HexCoord,
  state: GameState,
  map: SolarSystemMap,
  playerId: PlayerId,
  planning: AstrogationPlanningSnapshot,
  shiftKey: boolean,
): GameCommand[] => {
  const interaction = resolveAstrogationClick(
    state,
    map,
    playerId,
    planning,
    hex,
    shiftKey,
  );

  switch (interaction.type) {
    case 'weakGravityToggle':
      return [
        {
          type: 'setWeakGravityChoices',
          shipId: interaction.shipId,
          choices: interaction.choices,
        },
      ];
    case 'overloadToggle':
      return [
        {
          type: 'setOverloadDirection',
          shipId: interaction.shipId,
          direction: interaction.direction,
        },
      ];
    case 'burnToggle':
      return [
        {
          type: 'setBurnDirection',
          shipId: interaction.shipId,
          direction: interaction.direction,
        },
      ];
    case 'selectShip':
      return [
        {
          type: 'selectShip',
          shipId: interaction.shipId,
        },
      ];
    case 'toggleHexSelection':
      return [
        {
          type: 'toggleFleetSelection',
          shipIds: interaction.shipIds,
        },
      ];
    case 'steerFleet':
      return [
        {
          type: 'steerFleet',
          targetHex: interaction.targetHex,
        },
      ];
    case 'clearSelection':
      return [{ type: 'deselectShip' }];
    default: {
      const _exhaustive: never = interaction;
      return _exhaustive;
    }
  }
};

const interpretOrdnanceClick = (
  hex: HexCoord,
  state: GameState,
  playerId: PlayerId,
  planning: OrdnancePlanningSnapshot,
): GameCommand[] => {
  const interaction = resolveOrdnanceClick(state, playerId, planning, hex);

  switch (interaction.type) {
    case 'torpedoAccel':
      return [
        {
          type: 'setTorpedoAccel',
          direction: interaction.torpedoAccel,
          steps: interaction.torpedoAccelSteps,
        },
      ];
    case 'selectShip':
      return [
        {
          type: 'selectShip',
          shipId: interaction.shipId,
        },
        { type: 'clearTorpedoAcceleration' },
      ];
    case 'none':
      return [];
    default: {
      const _exhaustive: never = interaction;
      return _exhaustive;
    }
  }
};

const interpretClickHex = (
  hex: HexCoord,
  state: GameState | null,
  interactionMode: InteractionMode,
  map: SolarSystemMap | null,
  playerId: PlayerId,
  planning: InteractivePlanningSnapshot,
  shiftKey: boolean,
): GameCommand[] => {
  if (!state || !map) return [];

  if (state.activePlayer !== playerId) return [];

  switch (interactionMode) {
    case 'combat':
      return interpretCombatClick(hex, state, map, playerId, planning);
    case 'ordnance':
      return interpretOrdnanceClick(hex, state, playerId, planning);
    case 'astrogation':
      return interpretAstrogationClick(
        hex,
        state,
        map,
        playerId,
        planning,
        shiftKey,
      );
    default:
      return [];
  }
};

export const interpretInput = (
  event: InputEvent,
  state: GameState | null,
  interactionMode: InteractionMode,
  map: SolarSystemMap | null,
  playerId: PlayerId,
  planning: InteractivePlanningSnapshot,
): GameCommand[] => {
  switch (event.type) {
    case 'clickHex':
      return interpretClickHex(
        event.hex,
        state,
        interactionMode,
        map,
        playerId,
        planning,
        event.shiftKey ?? false,
      );
    case 'hoverHex':
      if (state) return [{ type: 'setHoverHex', hex: event.hex }];

      if (planning.hoverHex) return [{ type: 'setHoverHex', hex: null }];

      return [];
    default: {
      const _exhaustive: never = event;
      return _exhaustive;
    }
  }
};
